/**
 * `/notifications/channels` — where notifications go.
 *
 * The thing that shapes this whole screen: **`config` comes back masked.** The
 * webhook token is stars, the SMTP password is stars. Secrets travel inward
 * only. So the editor cannot pre-fill credentials for an existing channel, and
 * pretending otherwise — showing `******` in a text field the user then saves —
 * would write the literal stars back as the password.
 *
 * The edit form therefore leaves config empty and says what that means: leave it
 * alone to keep the current one, fill it in to replace the whole object.
 *
 * Empty is not the same as invisible, though. The masked copy is rendered
 * read-only — a line under the channel's name in the table, and a block above
 * the editor's box — because a screen that shows a channel's configuration
 * nowhere at all reads as a channel that has none.
 *
 * `notification.manage` is the most dangerous permission in the system after the
 * superadmin bypass, because whoever holds it can point a channel at an endpoint
 * they control. The screen says so once, at the top, rather than nowhere.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HubIcon from '@mui/icons-material/Hub';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type {
  ChannelDto,
  IncompatibilityPolicy,
  PayloadMode,
  SendTestResponse,
  Severity,
  TransportDto,
} from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorState } from '../../components/ErrorState';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import {
  createChannel,
  deleteChannel,
  listChannels,
  listTransports,
  sendChannelTest,
  updateChannel,
} from '../../api/notifications';
import { formatBytes } from '../../utils/format';

const SEVERITIES: Severity[] = ['info', 'warning', 'critical'];
const POLICIES: IncompatibilityPolicy[] = ['fail', 'degrade', 'drop_and_warn'];

/** The transport's example, as it should appear in the configuration box. */
function exampleJson(transport: TransportDto | undefined): string {
  if (!transport) return '';
  return JSON.stringify(transport.configHelp.example, null, 2);
}

/**
 * The masked configuration, as the server holds it.
 *
 * Read-only everywhere it appears. It is the only way to see what a channel is
 * actually pointed at, and it must never reach a field that gets saved — the
 * stars would go back as the credential.
 */
function maskedJson(config: Record<string, unknown>): string {
  return JSON.stringify(config, null, 2);
}

/** The values worth showing. A transport leaves nulls behind for what it has not got. */
function configEntries(config: Record<string, unknown>): [string, unknown][] {
  return Object.entries(config).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );
}

/** One line of it for the table, with the whole masked object behind a tooltip. */
function maskedSummary(config: Record<string, unknown>): string {
  const line = configEntries(config)
    .map(
      ([key, value]) =>
        `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`,
    )
    .join('  ');
  return line.length > 90 ? `${line.slice(0, 89)}…` : line;
}

interface ChannelForm {
  name: string;
  transport: string;
  description: string;
  config: string;
  payloadMode: PayloadMode;
  onIncompatible: IncompatibilityPolicy;
  minSeverity: Severity;
  isDefaultMailer: boolean;
  isEnabled: boolean;
}

const EMPTY_FORM: ChannelForm = {
  name: '',
  transport: '',
  description: '',
  config: '{\n  \n}',
  payloadMode: 'none',
  onIncompatible: 'degrade',
  minSeverity: 'info',
  isDefaultMailer: false,
  isEnabled: true,
};

/**
 * An existing channel as form values.
 *
 * `config` is deliberately **empty**, not the masked copy. An empty box means
 * "leave the configuration alone"; putting `******` into a field that saves
 * would write the literal stars back as the credential. The masked copy is
 * still shown — read-only, above the box, never in it.
 */
function formFor(channel: ChannelDto): ChannelForm {
  return {
    name: channel.name,
    transport: channel.transport,
    description: channel.description ?? '',
    config: '',
    payloadMode: channel.payloadMode,
    onIncompatible: channel.onIncompatible,
    minSeverity: channel.minSeverity,
    isDefaultMailer: channel.isDefaultMailer,
    isEnabled: channel.isEnabled,
  };
}

/** Severity labels, keyed by the API value. The value is the identifier. */
const SEVERITY_KEYS = {
  info: 'severity.info',
  warning: 'severity.warning',
  critical: 'severity.critical',
} as const;

type SeverityKey = (typeof SEVERITY_KEYS)[keyof typeof SEVERITY_KEYS];

function severityKeyFor(severity: string): SeverityKey | undefined {
  return (SEVERITY_KEYS as Record<string, SeverityKey | undefined>)[severity];
}

export default function ChannelsPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const { can } = useAuth();

  /** Falls back to the raw value for a severity this build does not know. */
  function severityLabel(severity: string): string {
    const key = severityKeyFor(severity);
    return key ? t(key) : severity;
  }
  const list = useListQuery({ defaultSort: 'name' });
  const channels = useAsync(() => listChannels(list.query), [list.key]);
  const transports = useAsync(() => listTransports(), []);

  const mayManage = can('notification.manage');
  const mayTest = can('notification.send_test');

  const crud = useCrudDialogs<ChannelDto, ChannelForm>({ blank: EMPTY_FORM, formFor });
  const { form, editing, deleting } = crud;
  const [testResult, setTestResult] = useState<SendTestResponse | null>(null);

  const transport = transports.data?.transports.find((entry) => entry.key === form.transport);

  /** `config` is free-form JSON the transport validates, so it is typed as JSON. */
  function parseConfig(): Record<string, unknown> | undefined {
    if (form.config.trim() === '' || form.config.trim() === '{}') return undefined;
    const parsed: unknown = JSON.parse(form.config);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error(t('channels.dialog.configNotObject'));
    }
    return parsed as Record<string, unknown>;
  }

  const save = useSubmit(async () => {
    const config = parseConfig();

    if (editing) {
      // Config omitted means "leave it alone" — the masked copy must never be
      // sent back, or the stars become the credential.
      const updated = await updateChannel(editing.id, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        payloadMode: form.payloadMode,
        onIncompatible: form.onIncompatible,
        minSeverity: form.minSeverity,
        isDefaultMailer: form.isDefaultMailer,
        isEnabled: form.isEnabled,
        ...(config ? { config } : {}),
      });
      channels.reload();
      return updated;
    }

    const created = await createChannel({
      name: form.name.trim(),
      transport: form.transport,
      config: config ?? {},
      description: form.description.trim() || null,
      payloadMode: form.payloadMode,
      onIncompatible: form.onIncompatible,
      minSeverity: form.minSeverity,
      isDefaultMailer: form.isDefaultMailer,
      isEnabled: form.isEnabled,
    });
    channels.reload();
    return created;
  });

  const remove = useSubmit(async () => {
    if (!deleting) return undefined;
    const result = await deleteChannel(deleting.id);
    crud.closeDelete();
    channels.reload();
    return result;
  });

  const test = useSubmit(async (channel: ChannelDto) => {
    const result = await sendChannelTest(channel.id);
    setTestResult(result);
    return result;
  });

  const toggleEnabled = useSubmit(async (channel: ChannelDto) => {
    const result = await updateChannel(channel.id, { isEnabled: !channel.isEnabled });
    channels.reload();
    return result;
  });

  /**
   * Offers the transport's example configuration, unless the box has been used.
   *
   * Nobody can guess that a webhook wants `{ "url": ... }`, and an empty
   * textarea does not tell them. "Unused" means empty, the starting skeleton, or
   * the example belonging to the transport being switched away from — anything
   * else is somebody's own text and must survive changing the dropdown.
   *
   * Never while editing: there an empty box is what means "keep the current
   * configuration", so prefilling it would arm a silent credential replacement.
   */
  function chooseTransport(key: string): void {
    const next = transports.data?.transports.find((entry) => entry.key === key);
    const unused =
      form.config.trim() === '' ||
      form.config === EMPTY_FORM.config ||
      form.config === exampleJson(transport);

    crud.patch({
      transport: key,
      config: editing === null && unused ? exampleJson(next) : form.config,
    });
  }

  function openCreate() {
    const first = transports.data?.transports[0];
    crud.openCreate({ transport: first?.key ?? '', config: exampleJson(first) });
  }

  return (
    <>
      <PageHeader
        title={t('channels.title')}
        description={t('channels.description')}
        actions={
          mayManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('channels.new')}
            </Button>
          )
        }
      />

      {mayManage && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          <AlertTitle>{t('channels.manageWarningTitle')}</AlertTitle>
          {t('channels.manageWarning')}
        </Alert>
      )}

      {testResult && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setTestResult(null)}>
          <AlertTitle>{t('channels.testTitle')}</AlertTitle>
          {/* One sentence, one key: the channel count decides the plural form,
              and Czech needs three where English needs two. */}
          {t('channels.testResult', {
            count: testResult.queued.length,
            sent: testResult.delivered.sent,
            failed: testResult.delivered.failed,
          })}
          {testResult.skipped.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {t('channels.testSkipped', {
                entries: testResult.skipped
                  .map((entry) => `${entry.channel} (${entry.reason})`)
                  .join(', '),
              })}
            </Box>
          )}
        </Alert>
      )}
      {test.error && <ErrorState error={test.error} />}
      {toggleEnabled.error && <ErrorState error={toggleEnabled.error} />}

      <AsyncContent state={channels}>
        {({ channels: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(channel) => channel.id}
            list={list}
            page={page}
            actionsWidth={120}
            empty={{
              icon: <HubIcon fontSize="inherit" />,
              title: t('channels.emptyTitle'),
              description: t('channels.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('channels.column.name'),
                sortable: true,
                render: (channel) => (
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {channel.name}
                      </Typography>
                      {channel.isDefaultMailer && (
                        <Tooltip title={t('channels.defaultMailerTooltip')}>
                          <Chip size="small" color="info" label={t('channels.defaultMailer')} />
                        </Tooltip>
                      )}
                    </Stack>
                    {channel.description && (
                      <Typography variant="caption" color="text.secondary">
                        {channel.description}
                      </Typography>
                    )}
                    {/* The masked config, read-only and never in a field that
                        saves. An empty box with nothing beside it is how a
                        correctly configured webhook gets reported as broken. */}
                    {configEntries(channel.config).length > 0 && (
                      <Tooltip
                        title={
                          <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 11 }}>
                            {maskedJson(channel.config)}
                          </Box>
                        }
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {maskedSummary(channel.config)}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                ),
              },
              {
                field: 'transport',
                label: t('channels.column.transport'),
                sortable: true,
                width: 130,
                render: (channel) => (
                  <Chip size="small" variant="outlined" label={channel.transport} />
                ),
              },
              {
                field: 'payload',
                label: t('channels.column.payload'),
                width: 140,
                render: (channel) => (
                  <Typography variant="caption" color="text.secondary">
                    {channel.payloadMode} / {channel.onIncompatible}
                  </Typography>
                ),
              },
              {
                field: 'minSeverity',
                label: t('channels.column.minSeverity'),
                width: 110,
                render: (channel) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={
                      channel.minSeverity === 'critical'
                        ? 'error'
                        : channel.minSeverity === 'warning'
                          ? 'warning'
                          : 'default'
                    }
                    label={severityLabel(channel.minSeverity)}
                  />
                ),
              },
              {
                field: 'enabled',
                label: t('channels.column.enabled'),
                width: 100,
                render: (channel) => (
                  <Switch
                    size="small"
                    checked={channel.isEnabled}
                    disabled={!mayManage || toggleEnabled.pending}
                    onChange={() => void toggleEnabled.submit(channel)}
                  />
                ),
              },
            ]}
            actions={(channel) => [
              {
                key: 'test',
                label: t('channels.sendTest'),
                icon: <SendIcon fontSize="small" />,
                onClick: () => void test.submit(channel),
                disabled: test.pending,
                hidden: !mayTest,
              },
              {
                key: 'edit',
                label: t('channels.edit'),
                icon: <EditOutlinedIcon fontSize="small" />,
                onClick: () => crud.openEdit(channel),
                hidden: !mayManage,
              },
              {
                key: 'delete',
                label: t('channels.delete'),
                icon: <DeleteOutlinedIcon fontSize="small" />,
                tone: 'error',
                onClick: () => crud.openDelete(channel),
                hidden: !mayManage,
              },
            ]}
          />
        )}
      </AsyncContent>

      <FormDialog
        open={crud.isOpen}
        title={
          editing
            ? t('channels.dialog.editTitle', { name: editing.name })
            : t('channels.dialog.createTitle')
        }
        submitLabel={editing ? t('common:action.save') : t('common:action.create')}
        maxWidth="md"
        pending={save.pending}
        error={save.error}
        disabled={form.name.trim() === '' || form.transport === ''}
        onClose={crud.close}
        onSubmit={async () => {
          if (await save.submit()) crud.close();
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('channels.dialog.name')}
            value={form.name}
            onChange={(event) => crud.patch({ name: event.target.value })}
            autoFocus
            fullWidth
          />
          <TextField
            select
            label={t('channels.dialog.transport')}
            value={form.transport}
            onChange={(event) => chooseTransport(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            {(transports.data?.transports ?? []).map((entry) => (
              <MenuItem key={entry.key} value={entry.key}>
                {entry.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TextField
          label={t('channels.dialog.description')}
          value={form.description}
          onChange={(event) => crud.patch({ description: event.target.value })}
          fullWidth
        />

        {transport && (
          <Alert severity="info" icon={false}>
            {/* Trans for the emphasised transport name; two sentences rather
                than one, so neither language has to carry a trailing clause. */}
            <Trans
              i18nKey="channels.dialog.carries"
              ns="notifications"
              count={transport.maxPayloadCount}
              values={{
                label: transport.label,
                modes: transport.payloadModes.join(', '),
                size: formatBytes(transport.maxPayloadBytes),
              }}
              components={{ strong: <strong /> }}
            />{' '}
            {transport.richText
              ? t('channels.dialog.richText')
              : t('channels.dialog.plainText')}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('channels.dialog.payloadMode')}
            value={form.payloadMode}
            onChange={(event) =>
              crud.patch({ payloadMode: event.target.value as PayloadMode })
            }
            fullWidth
          >
            {(transport?.payloadModes ?? (['none'] as PayloadMode[])).map((mode) => (
              <MenuItem key={mode} value={mode}>
                {mode}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('channels.dialog.onIncompatible')}
            value={form.onIncompatible}
            onChange={(event) =>
              crud.patch({ onIncompatible: event.target.value as IncompatibilityPolicy })
            }
            helperText={t('channels.dialog.onIncompatibleHelp')}
            fullWidth
          >
            {POLICIES.map((policy) => (
              <MenuItem key={policy} value={policy}>
                {policy}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('channels.dialog.minSeverity')}
            value={form.minSeverity}
            onChange={(event) => crud.patch({ minSeverity: event.target.value as Severity })}
            fullWidth
          >
            {SEVERITIES.map((severity) => (
              <MenuItem key={severity} value={severity}>
                {severityLabel(severity)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Box>
          {editing && (
            <Box
              sx={{
                mb: 1.5,
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t('channels.dialog.currentConfig')}
              </Typography>
              {configEntries(editing.config).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('channels.dialog.currentConfigEmpty')}
                </Typography>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {maskedJson(editing.config)}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('channels.dialog.currentConfigNote')}
              </Typography>
            </Box>
          )}

          <TextField
            label={t('channels.dialog.config')}
            value={form.config}
            onChange={(event) => crud.patch({ config: event.target.value })}
            multiline
            minRows={5}
            fullWidth
            slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: 13 } } }}
            helperText={
              editing
                ? t('channels.dialog.configHelpEditing')
                : t('channels.dialog.configHelpCreating')
            }
          />

          {transport && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {t('channels.dialog.expectsHere', { label: transport.label })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {transport.configHelp.summary}
              </Typography>

              {transport.configHelp.fields.length > 0 && (
                <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                  {transport.configHelp.fields.map((field) => (
                    <Stack
                      key={field.key}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'baseline' }}
                    >
                      <Box
                        component="code"
                        sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}
                      >
                        {field.key}
                      </Box>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={field.required ? 'primary' : 'default'}
                        label={
                          field.required
                            ? t('channels.dialog.required')
                            : t('channels.dialog.optional')
                        }
                      />
                      <Typography variant="body2" color="text.secondary">
                        {field.description}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}

              {transport.configHelp.fields.length > 0 && (
                <>
                  <Stack
                    direction="row"
                    sx={{ mt: 1.5, alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t('channels.dialog.exampleCaption')}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => crud.patch({ config: exampleJson(transport) })}
                    >
                      {t('channels.dialog.useExample')}
                    </Button>
                  </Stack>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      overflowX: 'auto',
                    }}
                  >
                    {exampleJson(transport)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isEnabled}
                onChange={(event) => crud.patch({ isEnabled: event.target.checked })}
              />
            }
            label={t('channels.dialog.enabled')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isDefaultMailer}
                onChange={(event) => crud.patch({ isDefaultMailer: event.target.checked })}
              />
            }
            label={t('channels.dialog.defaultMailer')}
          />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={deleting !== null}
        title={t('channels.deleteDialog.title', { name: deleting?.name ?? '' })}
        tone="error"
        confirmLabel={t('channels.deleteDialog.confirm')}
        pending={remove.pending}
        error={remove.error}
        onClose={crud.closeDelete}
        onConfirm={() => void remove.submit()}
        message={t('channels.deleteDialog.message')}
      />
    </>
  );
}
